import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Badge,
  Box,
  Button,
  Group,
  NumberInput,
  Stack,
  Switch,
  Table,
  Text,
  Textarea,
} from '@mantine/core';
import type { ParseEntityType } from '@seo/contracts';
import { publicMessageOf, useTRPC } from '../lib/trpc';
import classes from '../pages/EntityPage.module.css';

const ACTION_COLOR: Record<string, string> = {
  write: 'mint',
  review: 'amber',
  conflict: 'rose',
};

const ACTION_LABELS: Record<string, string> = {
  write: 'запише',
  review: 'на перевірку',
  conflict: 'конфлікт',
};

/** Renders whatever the parser proposed; values are `unknown` at this boundary. */
function show(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'string') return value === '' ? '—' : value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return JSON.stringify(value);
}

export interface ParsePanelProps {
  entityType: ParseEntityType;
  entityId: string;
  /** Fields the operator may ask the parser for — the consent list (§10). */
  targets: string[];
}

/**
 * AI parsing of an entity (§10), acceptance scenario 15.
 *
 * The panel shows the proposal before anything is written, with the previous value next to
 * the new one and a badge saying what the policy will do with it. `Перезаписувати заповнені`
 * is off by default and deliberately hard to miss: it is the switch that lets a model
 * replace a licence number somebody checked against a regulator's register.
 */
export function ParsePanel({ entityType, entityId, targets }: ParsePanelProps): React.JSX.Element {
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  const [source, setSource] = useState('');
  const [overwriteFilled, setOverwriteFilled] = useState(false);
  const [minConfidence, setMinConfidence] = useState(85);
  const [runId, setRunId] = useState<string | null>(null);

  const run = useQuery({
    ...trpc.parsing.byId.queryOptions({ id: runId ?? '' }),
    enabled: runId !== null,
  });

  /**
   * Історія прогонів цієї сутності.
   *
   * `parsing.runs` існувала без викликача, і без неї панель памʼятала рівно один прогін — той,
   * що зробили в цій вкладці. Після перезавантаження екрана від нього не лишалось нічого:
   * питання «що саме модель уже писала в цей бренд і коли» не мало відповіді, хоча відкат
   * (`parsing.revert`) саме на минулий прогін і розрахований.
   */
  const runs = useQuery(trpc.parsing.runs.queryOptions({ entityType, entityId }));

  const invalidate = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: trpc.parsing.pathKey() });
  };

  const start = useMutation(
    trpc.parsing.start.mutationOptions({
      onSuccess: async (result) => {
        setRunId(result.id);
        await invalidate();
      },
    }),
  );
  const applyRun = useMutation(trpc.parsing.applyRun.mutationOptions({ onSuccess: invalidate }));
  const revert = useMutation(trpc.parsing.revert.mutationOptions({ onSuccess: invalidate }));

  return (
    <Box className={classes.panel} data-testid="parse-panel">
      <Text className={classes.panelTitle} mb={8}>
        AI-парсинг
      </Text>

      <Textarea
        label="Джерело (текст або URL)"
        // Not `autosize`: it measures the element through a resize observer, which the
        // component test's DOM does not provide — and a screen that only works in a browser
        // is a screen whose regressions are found by a human.
        rows={3}
        value={source}
        onChange={(event) => {
          setSource(event.currentTarget.value);
        }}
        data-testid="parse-source"
        mb="sm"
      />

      <Group gap="md" align="flex-end" mb="sm">
        <Switch
          size="xs"
          label="Перезаписувати заповнені"
          checked={overwriteFilled}
          onChange={(event) => {
            setOverwriteFilled(event.currentTarget.checked);
          }}
          data-testid="parse-overwrite"
        />
        <NumberInput
          size="xs"
          label="Поріг впевненості, %"
          min={0}
          max={100}
          value={minConfidence}
          onChange={(value) => {
            setMinConfidence(typeof value === 'number' ? value : 85);
          }}
          data-testid="parse-confidence"
          style={{ width: 150 }}
        />
        <Button
          size="xs"
          disabled={source.trim() === ''}
          loading={start.isPending}
          data-testid="parse-start"
          onClick={() => {
            start.mutate({
              entityType,
              entityId,
              sources: [
                { kind: source.trim().startsWith('http') ? 'url' : 'text', value: source },
              ],
              targets,
              policy: { overwriteFilled, minConfidence },
            });
          }}
        >
          Розпарсити
        </Button>
      </Group>

      {start.error === null ? null : (
        <Text className={classes.danger} data-testid="parse-error">
          {publicMessageOf(start.error)}
        </Text>
      )}

      {run.data === undefined ? (
        <Text className={classes.dim} data-testid="parse-no-run">
          Прогону ще не було.
        </Text>
      ) : (
        <>
          <Table striped withRowBorders={false} verticalSpacing={4} fz="xs" mb="sm">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Поле</Table.Th>
                <Table.Th>Було</Table.Th>
                <Table.Th>Стане</Table.Th>
                <Table.Th>Впевненість</Table.Th>
                <Table.Th>Дія</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {run.data.fields.map((field) => (
                <Table.Tr key={field.id} data-testid={`parse-field-${field.field}`}>
                  <Table.Td className={classes.mono}>{field.field}</Table.Td>
                  <Table.Td className={classes.dim} data-testid={`parse-before-${field.field}`}>
                    {show(field.previousValue)}
                  </Table.Td>
                  <Table.Td data-testid={`parse-after-${field.field}`}>{show(field.value)}</Table.Td>
                  <Table.Td className={classes.mono}>{field.confidence}%</Table.Td>
                  <Table.Td>
                    <Badge
                      size="xs"
                      color={ACTION_COLOR[field.action] ?? 'graphite'}
                      data-testid={`parse-action-${field.field}`}
                    >
                      {ACTION_LABELS[field.action] ?? field.action}
                    </Badge>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>

          <Group gap="xs">
            <Button
              size="xs"
              disabled={run.data.appliedAt !== null}
              loading={applyRun.isPending}
              data-testid="parse-apply"
              onClick={() => {
                if (runId !== null) applyRun.mutate({ id: runId });
              }}
            >
              Застосувати
            </Button>
            <Button
              size="xs"
              variant="default"
              disabled={run.data.appliedAt === null}
              loading={revert.isPending}
              data-testid="parse-revert"
              onClick={() => {
                if (runId !== null) revert.mutate({ id: runId });
              }}
            >
              Відкотити
            </Button>
            {applyRun.data === undefined ? null : (
              <Stack gap={2}>
                <Text className={classes.dim} data-testid="parse-written">
                  записано: {applyRun.data.written}
                </Text>
                {applyRun.data.skipped.length === 0 ? null : (
                  <Text className={classes.warn} data-testid="parse-skipped">
                    пропущено:{' '}
                    {applyRun.data.skipped.map((row) => `${row.field} — ${row.reason}`).join('; ')}
                  </Text>
                )}
              </Stack>
            )}
          </Group>
        </>
      )}
      {(runs.data ?? []).length === 0 ? null : (
        <Box mt="sm" data-testid="parse-runs">
          <Text className={classes.dim} mb={4}>
            Минулі прогони
          </Text>
          <Stack gap={2}>
            {(runs.data ?? []).slice(0, 5).map((row) => (
              <Group key={row.id} gap={8} data-testid={`parse-run-${row.id}`}>
                <Text className={classes.mono}>
                  {new Date(row.createdAt).toISOString().slice(0, 16).replace('T', ' ')}
                </Text>
                <Badge size="xs" color={row.status === 'applied' ? 'mint' : 'slate'}>
                  {row.status}
                </Badge>
                <Text className={classes.dim}>{row.targets.join(', ')}</Text>
                {/*
                  * Відкрити минулий прогін — це і є вхід у відкат: `parsing.revert` працює саме
                  * з ним, а не з тим, що зараз у вкладці.
                  */}
                <Button
                  size="compact-xs"
                  variant="subtle"
                  data-testid={`parse-run-open-${row.id}`}
                  onClick={() => {
                    setRunId(row.id);
                  }}
                >
                  відкрити
                </Button>
              </Group>
            ))}
          </Stack>
        </Box>
      )}
    </Box>
  );
}
